import styled, { css } from 'styled-components';

import {
  IMarginProps,
  marginPropsStyles,
} from '~/shared/interfaces/IMarginProps';
import { TInputSize } from './types';

interface IInputWrapperProps {
  isTypePassword: boolean;
  hasIcon: boolean;
  hasError: boolean;
  size: TInputSize;
  isDisabled: boolean;
}

interface IIconWrapperProps {
  inputSize: TInputSize;
}

interface IEndContentWrapperProps {
  inputSize: TInputSize;
}

interface IPasswordButtonProps {
  inputSize: TInputSize;
}

interface ICopyButtonProps {
  inputSize: TInputSize;
}

export const Container = styled.label<IMarginProps>`
  ${marginPropsStyles}
`;

const getHeight: Record<TInputSize, string> = {
  lg: '48px',
  md: '42px',
  sm: '36px',
};

export const InputWrapper = styled.div<IInputWrapperProps>`
  display: flex;
  align-items: center;

  height: ${({ size }) => getHeight[size]};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => `1px solid ${theme.colors.gray[100]}`};

  transition: all ease-in 0.2s;
  overflow: hidden;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  ${({ theme, hasError }) =>
    hasError &&
    css`
      border-color: ${theme.colors.danger} !important;

      svg {
        color: ${theme.colors.danger} !important;
      }
    `}

  ${({ isDisabled, theme }) =>
    isDisabled &&
    css`
      background-color: ${theme.colors.gray[50]};
    `}

  > input {
    background: none;
    height: 100%;
    flex: 1;
    padding: ${({ theme, size }) =>
      `0 ${size === 'lg' ? theme.spacing.sm : '12px'}`};
    font-size: ${({ size }) => (size === 'sm' ? '12px' : '14px')};
    line-height: ${({ size }) => getHeight[size]};
    color: ${({ theme }) => theme.colors.onSurface};

    ${({ isTypePassword }) =>
      isTypePassword &&
      css`
        padding-right: 0;
      `}

    ${({ hasIcon }) =>
      hasIcon &&
      css`
        padding-left: 0;
      `}

    &::placeholder {
      color: ${({ theme }) => theme.colors.gray[200]};
    }
  }
`;

export const IconWrapper = styled.div<IIconWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 100%;
  width: ${({ inputSize }) => getHeight[inputSize]};

  > svg {
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.onSurface};
  }
`;

export const EndContentWrapper = styled.div<IEndContentWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 100%;
  min-width: ${({ inputSize }) => getHeight[inputSize]};

  > svg {
    min-width: 18px;
    min-height: 18px;
    color: ${({ theme }) => theme.colors.onSurface};
  }
`;

export const PasswordButton = styled.button.attrs({
  type: 'button',
})<ICopyButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 100%;
  width: ${({ inputSize }) => getHeight[inputSize]};

  > svg {
    min-width: 18px;
    min-height: 18px;
    color: ${({ theme }) => theme.colors.onSurface};
  }
`;

export const CopyButton = styled.button.attrs({
  type: 'button',
})<IPasswordButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-weight: 400;

  width: ${({ inputSize }) => getHeight[inputSize]};
  height: 100%;

  > svg {
    min-width: 16px;
    min-height: 16px;
    color: ${({ theme }) => theme.colors.onSurface};
  }
`;
